import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { DateTime } from "luxon"
import {
    SelectedMainUserState,
    SelectedPriceListItem,
    CreateOrderDialogFlow,
    CreateOrderDialogType,
} from "../../../createOrder/createOrderTypes"
import { DeliveryTimesField, DynamicConfigKey, MeetingServiceTabValues } from "../../components/form/formTypes"
import { ReceiptConfirmOrderDialogWrap } from "./ReceiptConfirmOrderDialogWrap"
import { OrderContextState, OrderFormStatus } from "../../mainForm/helpers"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { DeadlineMessageDialog } from "./DeadlineMessageDialog"
import {
    DeadlineMessageQuery,
    DeadlineMessageResponse,
    fetchDeadlineMessage,
} from "../../components/shop/delivery/deliveryApi"
import { CreateOrderType } from "../../helpers"

const transformData = (data: DeadlineMessageResponse) => {
    return data.deadlineMessage
}

type ReceiptConfirmOrderDialogControllerProps = Readonly<{
    onClose: () => void
    dialogType: CreateOrderDialogType
    orderDetails: MeetingServiceTabValues
    mainUser: SelectedMainUserState
    handleCloseMainDialog: () => void
    handleOpenResourceBooking: (type: CreateOrderType) => void
    handleAddNewMeetingService: () => void
    orderContext: OrderContextState
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    flowConfig?: CreateOrderDialogFlow
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}>

export function ReceiptConfirmOrderDialogController({
    onClose,
    dialogType,
    orderDetails,
    mainUser,
    orderContext,
    setOrderContext,
    handleCloseMainDialog,
    handleOpenResourceBooking,
    handleAddNewMeetingService,
    flowConfig,
    orderFormStatusRef,
}: ReceiptConfirmOrderDialogControllerProps) {
    const [showReceiptDialog, setShowReceiptDialog] = useState(false)

    const multipleDeliveries: DeliveryTimesField = orderDetails[DynamicConfigKey.MULTIPLE_DELIVERIES]!
    const deliveryDate = multipleDeliveries && multipleDeliveries.deliveryDate
    const deliveryTime = DateTime.fromISO(`${deliveryDate}T${multipleDeliveries.deliveryTimes[0].time}`).toISO({
        suppressSeconds: true,
        includeOffset: false,
    })

    const priceList: SelectedPriceListItem = orderDetails["priceList"]

    const hasProductWish =
        multipleDeliveries?.deliveryTimes?.some((delivery) =>
            delivery.orderLines?.some((line) => line.type === DynamicConfigKey.PRODUCT_WISH),
        ) ?? false

    const { data: messageInfo, isLoading } = useApiQuery(fetchDeadlineMessage, {
        queryName: DeadlineMessageQuery.DELIVERY_DEADLINE_MESSAGE,
        dependencies: {
            merchantId: priceList.merchantId,
            deliveryTime,
            productWish: hasProductWish,
            priceListId: priceList.priceListId,
            shopId: priceList.shopId,
        },
        select: transformData,
        enabled: !!deliveryDate,
    })

    useEffect(() => {
        if (!isLoading && messageInfo) {
            if (messageInfo.isConfirmed) {
                setShowReceiptDialog(true)
            }
        }
    }, [isLoading, messageInfo])

    if (isLoading || !messageInfo) {
        return null
    }

    if (!messageInfo.isConfirmed && !showReceiptDialog) {
        return (
            <DeadlineMessageDialog
                message={messageInfo.message}
                onClose={onClose}
                handlePlaceOrder={() => setShowReceiptDialog(true)}
            />
        )
    }
    if (showReceiptDialog) {
        return (
            <>
                <ReceiptConfirmOrderDialogWrap
                    orderContext={orderContext}
                    setOrderContext={setOrderContext}
                    open={showReceiptDialog}
                    onClose={onClose}
                    handleCloseMainDialog={handleCloseMainDialog}
                    handleOpenResourceBooking={handleOpenResourceBooking}
                    handleAddNewMeetingService={handleAddNewMeetingService}
                    dialogType={dialogType}
                    orderDetails={orderDetails}
                    mainUser={mainUser}
                    flowConfig={flowConfig}
                    orderFormStatusRef={orderFormStatusRef}
                />
            </>
        )
    }
    return null
}
